import { useParams, Link } from 'react-router-dom'
import Navbar from '@/components/feature/Navbar'
import Footer from '@/components/feature/Footer'
import SEOHead from '@/components/feature/SEOHead'
import Breadcrumb from '@/components/feature/Breadcrumb'
import ComicCard from '@/components/feature/ComicCard'
import { comics, categories } from '@/mocks/comics'

const categoryNameMap: Record<string, string> = {
  love: '恋爱', fantasy: '奇幻', action: '动作', suspense: '悬疑',
  comedy: '搞笑', horror: '惊悚', daily: '日常', history: '古风',
}

const categoryDescMap: Record<string, string> = {
  love: '甜蜜浪漫的恋爱故事，感受心动的每一个瞬间',
  fantasy: '奇幻世界的冒险旅程，魔法与传说交织的精彩篇章',
  action: '热血激战燃到爆，最强的战斗漫画尽在这里',
  suspense: '烧脑推理悬疑大作，每一话都让你欲罢不能',
  comedy: '爆笑日常欢乐不断，轻松解压的首选',
  horror: '细思极恐的恐怖故事，挑战你的胆量极限',
  daily: '温馨治愈的日常篇章，平凡生活中的温暖与感动',
  history: '唯美古风穿越之旅，穿越时空的浪漫邂逅',
}

const categoryEmojiMap: Record<string, string> = {
  love: '💕', fantasy: '✨', action: '⚔️', suspense: '🔍',
  comedy: '😂', horror: '👻', daily: '🌿', history: '🏯',
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>()
  const categoryName = (slug && categoryNameMap[slug]) || '漫画'
  const categoryDesc = (slug && categoryDescMap[slug]) || ''
  const categoryEmoji = (slug && categoryEmojiMap[slug]) || '📚'
  const filteredComics = slug ? comics.filter((c) => c.category === slug) : comics
  const categoryCount = categories.find((c) => c.slug === slug)?.count || filteredComics.length

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 韩漫画网`,
    description: `韩漫画网${categoryName}漫画分类，共${categoryCount}+部作品免费在线观看。${categoryDesc}`,
    url: `http://eocesp.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'http://eocesp.cn/',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: filteredComics.length,
      itemListElement: filteredComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Book',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `http://eocesp.cn/comic/${comic.id}`,
        },
      })),
    },
  }

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画 - 韩漫画网 - 免费在线观看韩国${categoryName}漫画`}
        description={`韩漫画网${categoryName}漫画分类，共${categoryCount}+部${categoryName}类韩漫作品免费在线观看。${categoryDesc}`}
        keywords={`韩国${categoryName}漫画,${categoryName}韩漫,免费${categoryName}漫画,韩漫画网`}
        canonicalUrl={`http://eocesp.cn/category/${slug}`}
        schema={schema}
      />
      <Navbar />

      <main className="pt-16">
        <section className="py-16 md:py-24 bg-background-50">
          <div className="px-4 md:px-6 max-w-[1400px] mx-auto text-center">
            <div className="mb-6">
              <span className="text-5xl md:text-7xl">{categoryEmoji}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading text-foreground-900 mb-4">
              {categoryName}漫画
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto leading-relaxed">
              {categoryDesc}
            </p>
            <p className="mt-3 text-xs text-foreground-500">
              共 <strong className="text-primary-500 font-semibold">{categoryCount}+</strong> 部作品
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-[1400px] mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: `${categoryName}漫画` },
                ]}
              />
            </div>

            <div className="flex items-center gap-4 mb-8 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    author={comic.author}
                    rating={comic.rating}
                    views={comic.views}
                    status={comic.status}
                    coverImage={comic.coverImage}
                    category={comic.category}
                    tags={comic.tags}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
                </div>
                <p className="text-foreground-600 text-sm">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}